import React, { useState } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';

export type CompetitorWeight = { competitorId: number, weight: number };
export default function CompetitorWeightsDialog({ hotel, weights, canWrite, onApply, onClose }: {
    hotel: CompanyHotel, weights: CompetitorWeight[], canWrite: boolean,
    onApply: (weights: CompetitorWeight[]) => void, onClose: () => void,
}) {
    const [draft, setDraft] = useState(() => hotel.competitors.map(c => ({
        competitorId: c.id, name: c.name, value: String(weights.find(w => w.competitorId === c.id)?.weight ?? 1),
    })));
    const valid = draft.every(w => /^(?:[0-9]|10)$/.test(w.value));
    const total = valid ? draft.reduce((sum,w) => sum + Number(w.value), 0) : 0;
    return <Dialog open onClose={onClose} className="relative z-[100]">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
            <DialogPanel className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
                <DialogTitle className="text-lg font-semibold">Competitor weights</DialogTitle>
                <p className="mt-2 text-sm text-gray-600">Use whole numbers from 0 to 10. Set 0 to exclude a competitor from Weighted average.</p>
                {!!draft.length && <>
                    <p className="mt-2 text-sm text-gray-600">Higher weights give a competitor more influence. Shares are calculated automatically.</p>
                    <table className="mt-4 w-full table-fixed text-sm">
                        <colgroup><col /><col className="w-20" /><col className="w-[4.25rem]" /></colgroup>
                        <thead>
                            <tr className="border-b border-gray-200 text-gray-600">
                                <th scope="col" className="pb-2 pr-3 text-left font-medium">Competitor</th>
                                <th scope="col" className="pb-2 text-left font-medium">Weight</th>
                                <th scope="col" className="pb-2 pl-3 text-right font-medium">Share</th>
                            </tr>
                        </thead>
                        <tbody>
                            {draft.map((w,index) => <tr key={w.competitorId}>
                                <td className="pt-3 pr-3 break-words">
                                    <label htmlFor={`weight-${w.competitorId}`}>{w.name}</label>
                                </td>
                                <td className="pt-3">
                                    <input id={`weight-${w.competitorId}`} type="number" min="0" max="10" step="1" value={w.value}
                                        disabled={!canWrite} aria-invalid={!/^(?:[0-9]|10)$/.test(w.value)}
                                        className="w-full rounded border border-gray-300 p-2 text-sm disabled:bg-gray-100"
                                        onChange={e => setDraft(draft.map((entry,i) => i === index ? {...entry,value:e.target.value} : entry))} />
                                </td>
                                <td className="pt-3 pl-3 text-right tabular-nums">{valid ? (total ? Number(w.value)/total*100 : 0).toFixed(1)+'%' : '—'}</td>
                            </tr>)}
                        </tbody>
                    </table>
                </>}
                {!draft.length && <p className="mt-4 text-sm">No competitors yet. New competitors start with a weight of 1.</p>}
                {!valid && <p role="alert" className="mt-3 text-sm text-red-600">Enter a whole number from 0 to 10 for every competitor.</p>}
                {canWrite && <p className="mt-4 text-sm text-gray-500">Apply these weights, then Save the calendar settings to activate them.</p>}
                <div className="mt-6 flex justify-end gap-3">
                    <button type="button" onClick={onClose} className="rounded border border-gray-300 px-4 py-2 text-sm">{canWrite ? 'Cancel' : 'Close'}</button>
                    {canWrite && <button type="button" disabled={!valid} onClick={() => onApply(draft.map(w => ({competitorId:w.competitorId,weight:Number(w.value)})))} className="rounded bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50">Apply weights</button>}
                </div>
            </DialogPanel>
        </div>
    </Dialog>;
}
